/**
 * O que a faixa da contingência diz (`R-06`, ADR-077). Puro: as frases saem só do que a API
 * mandou — a regra (quem abre, a ordem da fila, o prazo que não para) é do servidor.
 */
import type { Contingencia } from "../api/tipos";
import { horaEmBrasilia, restante } from "../formato";

export type Cor = "vermelho" | "amarelo" | "verde";

export type TextoDaFaixa = {
  cor: Cor;
  titulo: string;
  linhas: string[];
  /** Há fila para tentar agora. */
  tentar: boolean;
  /** Para onde o "Ver a fila" leva. */
  link: string | null;
};

/** Menos que isto para o mais antigo perder as 24 h, e a faixa fica vermelha. */
export const PERTO_DAS_24H_MS = 4 * 3_600_000;

function envios(n: number): string {
  return n === 1 ? "1 envio" : `${n} envios`;
}

function perderam(n: number): string {
  return n === 1
    ? "1 passagem perdeu as 24 h na fila"
    : `${n} passagens perderam as 24 h na fila`;
}

/** A faixa aparece? Fora do ar, ou de volta há pouco (o servidor diz por quanto tempo). */
export function faixaVisivel(c: Contingencia | null): boolean {
  return !!c && ((c.aberta && c.desde !== null) || c.voltou_em !== null);
}

export function textoDaFaixa(
  c: Contingencia | null,
  agora: Date,
): TextoDaFaixa | null {
  if (!c) return null;
  const linhas: string[] = [];
  const link =
    c.na_fila > 0
      ? "/senatran?cartao=na_fila_da_contingencia"
      : c.venceram > 0
        ? "/senatran?cartao=venceram_na_contingencia"
        : null;
  if (c.aberta && c.desde) {
    if (c.na_fila > 0) {
      let frase = `${envios(c.na_fila)} na fila: ${c.na_fila === 1 ? "sai" : "saem"} sozinho${c.na_fila === 1 ? "" : "s"}, pelo prazo de 24 h, quando ela voltar.`;
      if (c.perde_as_24h_em)
        frase += ` O mais antigo perde as 24 h às ${horaEmBrasilia(c.perde_as_24h_em, agora)} (${restante(c.perde_as_24h_em, agora)}).`;
      linhas.push(frase);
    } else {
      linhas.push(
        "Nada espera na fila. A contingência fecha quando ela responder à próxima tentativa.",
      );
    }
    if (c.venceram > 0) linhas.push(`${perderam(c.venceram)}.`);
    if (c.proxima_tentativa_em)
      linhas.push(
        `O cockpit tenta de novo às ${horaEmBrasilia(c.proxima_tentativa_em, agora)}` +
          ` (${c.tentativas === 1 ? "1 tentativa" : `${c.tentativas} tentativas`} sem resposta).`,
      );
    const perto =
      c.perde_as_24h_em !== null &&
      new Date(c.perde_as_24h_em).getTime() - agora.getTime() <
        PERTO_DAS_24H_MS;
    return {
      cor: c.venceram > 0 || perto ? "vermelho" : "amarelo",
      titulo: `A SENATRAN não responde desde ${horaEmBrasilia(c.desde, agora)}: o cockpit está em contingência.`,
      linhas,
      // Sem fila também: o *Tentar agora* pergunta a ela pela consulta de uma passagem.
      tentar: true,
      link,
    };
  }
  if (c.voltou_em) {
    linhas.push(
      c.na_fila > 0
        ? `${envios(c.na_fila)} ainda saindo da fila, pelo prazo de 24 h.`
        : "A fila da contingência saiu.",
    );
    if (c.venceram > 0) linhas.push(`${perderam(c.venceram)}.`);
    const desde = c.desde
      ? ` (fora do ar desde ${horaEmBrasilia(c.desde, agora)})`
      : "";
    return {
      cor: c.venceram > 0 ? "amarelo" : "verde",
      titulo: `A SENATRAN voltou às ${horaEmBrasilia(c.voltou_em, agora)}${desde}.`,
      linhas,
      tentar: false,
      link,
    };
  }
  return null;
}
